<template>
  <div class="omap" :class="{ 'omap--pick': pickMode }">
    <l-map
      ref="lmap"
      :zoom="zoom"
      :center="center"
      :options="mapOptions"
      style="height: 100%; width: 100%;"
      @click="onMapClick"
    >
      <l-tile-layer :url="tileUrl" :attribution="attribution" />

      <template v-for="o in objects">
        <l-circle-marker
          v-if="o.hasTask"
          :key="'ring-' + o.id"
          :lat-lng="o.latLng"
          :radius="13"
          :color="ringColor"
          :weight="2"
          :fill="false"
          :interactive="false"
        />
        <l-circle-marker
          v-if="o.selected"
          :key="'sel-' + o.id"
          :lat-lng="o.latLng"
          :radius="18"
          :color="selectedColor"
          :weight="3"
          :fill="false"
          :interactive="false"
        />
        <l-circle-marker
          :key="'obj-' + o.id"
          :lat-lng="o.latLng"
          :radius="9"
          color="#ffffff"
          :weight="3"
          :fill="true"
          :fill-color="o.color"
          :fill-opacity="1"
          @click="onObjectClick(o)"
        >
          <l-tooltip>{{ o.name }} — {{ o.stateLabel }}</l-tooltip>
        </l-circle-marker>
      </template>

      <l-marker v-for="p in points" :key="'pt-' + p.id" :lat-lng="p.latLng" :icon="pointIcon(p)" @click="onPointClick(p)">
        <l-tooltip>{{ p.name }}</l-tooltip>
      </l-marker>
    </l-map>

    <div v-if="pickMode" class="omap__hint" role="status">
      Кликните на карту, чтобы выбрать координаты · Esc — отмена
      <button type="button" class="btn btn-sm btn-light ml-2" @click="$emit('pick-cancel')">Отмена</button>
    </div>

    <slot />
  </div>
</template>

<script>
import { LMap, LTileLayer, LCircleMarker, LMarker, LTooltip } from 'vue2-leaflet';
import { divIcon } from 'leaflet';
import { RING_COLOR, SELECTED_COLOR } from '@/components/management/dispatcher/objectState';

// The initial view is always Moscow and its region (spec §8): a fixed centre and zoom, never a
// fit over the objects — one far-away object would otherwise zoom the whole map out to nothing.
const DEFAULT_CENTER = [55.7558, 37.6173];
const DEFAULT_ZOOM = 9;

/**
 * The DOM node of a custom-point marker. Built as an Element, never as an html string: Leaflet's
 * `DivIcon.createIcon` assigns a string `html` through `innerHTML`, where an inline `on*` attribute
 * smuggled in through the colour would execute. `p.color` comes from `groupColor` (allowlisted), and
 * assigning it through `style.background` drops anything the engine does not parse as a colour.
 */
export function pointIconElement(p) {
  const el = document.createElement('div');
  el.className = `omap-point${p.hasTask ? ' omap-point--task' : ''}${p.selected ? ' omap-point--selected' : ''}`;
  el.style.background = p.color;
  return el;
}

export default {
  name: 'ObjectsMap',
  components: { LMap, LTileLayer, LCircleMarker, LMarker, LTooltip },
  props: {
    objects: { type: Array, default: () => [] },
    points: { type: Array, default: () => [] },
    pickMode: { type: Boolean, default: false },
  },
  data() {
    return {
      zoom: DEFAULT_ZOOM,
      center: DEFAULT_CENTER,
      tileUrl: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      mapOptions: { zoomControl: true },
      ringColor: RING_COLOR,
      selectedColor: SELECTED_COLOR,
    };
  },
  watch: {
    pickMode: {
      immediate: true, // a component mounted already in pick mode still gets the Esc listener
      handler(on) {
        if (on) window.addEventListener('keydown', this.onKeydown);
        else window.removeEventListener('keydown', this.onKeydown);
      },
    },
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.onKeydown);
  },
  methods: {
    mapObject() {
      return this.$refs.lmap && this.$refs.lmap.mapObject;
    },
    pointIcon(p) {
      return divIcon({
        className: 'omap-point-icon', html: pointIconElement(p), iconSize: [14, 14], iconAnchor: [7, 7],
      });
    },
    invalidateSize() {
      const map = this.mapObject();
      if (map) this.$nextTick(() => map.invalidateSize());
    },
    panTo(latLng, zoom) {
      const map = this.mapObject();
      if (!map) return;
      if (typeof zoom === 'number') map.setView(latLng, Math.max(map.getZoom(), zoom));
      else map.panTo(latLng);
    },
    onMapClick(e) {
      if (this.pickMode && e && e.latlng) this.$emit('pick', { lat: e.latlng.lat, lng: e.latlng.lng });
    },
    onObjectClick(o) {
      if (!this.pickMode) this.$emit('select-object', o.id);
    },
    onPointClick(p) {
      // In pick mode a point swallows the map click, so it has to pick its own coordinates.
      if (this.pickMode) {
        this.$emit('pick', { lat: p.latLng[0], lng: p.latLng[1] });
        return;
      }
      this.$emit('select-point', p.id);
    },
    onKeydown(e) {
      if (e.key === 'Escape') this.$emit('pick-cancel');
    },
  },
};
</script>

<style scoped lang="scss">
/* z-index: 0 caps the whole map subtree (Leaflet panes/controls go up to 1000) so it
   never paints over the fixed topnav (z-index 100) and its dropdowns. */
.omap { position: relative; z-index: 0; height: 100%; width: 100%; border-radius: 8px; overflow: hidden; }
.omap__hint {
  position: absolute; top: 12px; left: 50%; transform: translateX(-50%); z-index: 1001;
  background: #171a19; color: #fff; font-size: 12.5px; font-weight: 600; padding: 8px 12px; border-radius: 8px;
  display: flex; align-items: center; box-shadow: 0 6px 18px rgba(20, 24, 20, .25);
}
</style>

<style lang="scss">
/* Rendered by Leaflet outside the component's scope */
.omap--pick .leaflet-container { cursor: crosshair; }
.omap-point-icon { background: transparent; border: 0; }
.omap-point {
  width: 14px; height: 14px; transform: rotate(45deg); box-shadow: 0 0 0 2px #fff;
  &--task { box-shadow: 0 0 0 2px #fff, 0 0 0 4px #171a19; } /* same ring as an object with an active task */
  &--selected { box-shadow: 0 0 0 2px #fff, 0 0 0 4px #17876f; }
}
/* Only outside pick mode: otherwise a point would override the crosshair shown while picking. */
.omap:not(.omap--pick) .omap-point { cursor: pointer; }
</style>
